import { useEffect, useMemo, useState } from "react";
import type {
  AdminProduct,
  ProductCategory,
  ProductDraft,
  ProductEdition,
  ProductVersion,
} from "@/pages/admin/products/hooks/useAdminProducts";

interface ProductFormModalProps {
  product: AdminProduct | null;
  categories: ProductCategory[];
  onClose: () => void;
  onSubmit: (draft: ProductDraft) => Promise<void>;
  notify: (text: string, tone?: "success" | "error") => void;
}

const ICON_OPTIONS = [
  "ri-apps-2-line",
  "ri-code-s-slash-line",
  "ri-database-2-line",
  "ri-shield-keyhole-line",
  "ri-cloud-line",
  "ri-bar-chart-box-line",
  "ri-robot-2-line",
  "ri-window-2-line",
];

const STATUS_OPTIONS = [
  { value: "active", label: "已上架" },
  { value: "inactive", label: "已下架" },
  { value: "draft", label: "草稿" },
];

function emptyDraft(): ProductDraft {
  return {
    name: "",
    slug: "",
    category: "",
    icon: "ri-apps-2-line",
    summary: "",
    description: "",
    latest_version: "",
    versions: [{ version: "1.0.0", date: new Date().toISOString().slice(0, 10), notes: "首个正式版本" }],
    editions: [{ key: "standard", label: "标准版", price: 0 }],
    rating: 5,
    downloads: 0,
    status: "draft",
    sort_order: 0,
  };
}

function toDraft(product: AdminProduct | null): ProductDraft {
  if (!product) return emptyDraft();
  return {
    id: product.id,
    name: product.name,
    slug: product.slug,
    category: product.category ?? "",
    icon: product.icon || "ri-apps-2-line",
    summary: product.summary ?? "",
    description: product.description ?? "",
    latest_version: product.latest_version ?? "",
    versions: product.versions ?? [],
    editions: product.editions ?? [],
    rating: Number(product.rating) || 0,
    downloads: product.downloads ?? 0,
    status: product.status ?? "draft",
    sort_order: product.sort_order ?? 0,
  };
}

export default function ProductFormModal({ product, categories, onClose, onSubmit, notify }: ProductFormModalProps) {
  const [draft, setDraft] = useState<ProductDraft>(() => toDraft(product));
  const [saving, setSaving] = useState(false);

  const categoryOptions = useMemo(() => {
    const names = categories.map((item) => item.name);
    // 若产品携带了清单外的历史分类，保留为可选项，避免打开即丢失。
    if (draft.category && !names.includes(draft.category)) return [draft.category, ...names];
    return names;
  }, [categories, draft.category]);

  useEffect(() => {
    setDraft(toDraft(product));
  }, [product]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  const update = (patch: Partial<ProductDraft>) => setDraft((prev) => ({ ...prev, ...patch }));

  const updateVersion = (index: number, patch: Partial<ProductVersion>) => {
    setDraft((prev) => ({
      ...prev,
      versions: prev.versions.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const updateEdition = (index: number, patch: Partial<ProductEdition>) => {
    setDraft((prev) => ({
      ...prev,
      editions: prev.editions.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));
  };

  const handleSubmit = async () => {
    if (!draft.name.trim()) {
      notify("请填写产品名称", "error");
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        ...draft,
        name: draft.name.trim(),
        slug: draft.slug.trim(),
        versions: draft.versions.filter((item) => item.version.trim()),
        editions: draft.editions.filter((item) => item.label.trim()),
      });
      notify(product ? "软件产品已更新" : "软件产品已创建", "success");
      onClose();
    } catch (err) {
      notify(err instanceof Error ? err.message : "保存失败", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-foreground-950/50 p-0 sm:items-center sm:p-4">
      <button type="button" aria-label="关闭" className="absolute inset-0 cursor-default" onClick={onClose} />
      <div className="lh-fade-up relative max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-t-lg border border-background-200 bg-background-50 sm:rounded-lg">
        <div className="sticky top-0 flex items-start justify-between gap-4 border-b border-background-200 bg-background-50 px-5 py-4 md:px-6">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
              <i className={`${draft.icon || "ri-apps-2-line"} text-lg`} />
            </span>
            <div>
              <h2 className="font-heading text-lg font-semibold text-foreground-950">
                {product ? "编辑软件产品" : "新增软件产品"}
              </h2>
              <p className="mt-0.5 text-xs text-foreground-500">
                维护产品基本信息、版本历史与授权类型，并控制上架状态。
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100"
            aria-label="关闭"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="space-y-6 px-5 py-5 md:px-6">
          <section className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">产品名称 *</span>
              <input
                type="text"
                value={draft.name}
                onChange={(event) => update({ name: event.target.value })}
                placeholder="例如：LicenseHub 企业版"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">分类</span>
              <select
                value={draft.category}
                onChange={(event) => update({ category: event.target.value })}
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              >
                <option value="">未分类</option>
                {categoryOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <span className="text-[11px] text-foreground-400">
                分类来自「产品分类管理」，如需新增请先在上方分类面板添加。
              </span>
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">路由标识 (slug)</span>
              <input
                type="text"
                value={draft.slug}
                onChange={(event) => update({ slug: event.target.value })}
                placeholder="留空自动生成"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">上架状态</span>
              <select
                value={draft.status}
                onChange={(event) => update({ status: event.target.value })}
                className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </section>

          <section>
            <span className="text-xs font-medium text-foreground-700">产品图标</span>
            <div className="mt-2 flex flex-wrap gap-2">
              {ICON_OPTIONS.map((icon) => {
                const active = draft.icon === icon;
                return (
                  <button
                    key={icon}
                    type="button"
                    onClick={() => update({ icon })}
                    className={`flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border text-lg transition-colors ${
                      active
                        ? "border-primary-400 bg-primary-100 text-primary-700"
                        : "border-background-300 bg-background-50 text-foreground-600 hover:border-primary-300"
                    }`}
                    aria-label={`选择图标 ${icon}`}
                  >
                    <i className={icon} />
                  </button>
                );
              })}
            </div>
          </section>

          <section className="grid gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">一句话简介</span>
              <input
                type="text"
                value={draft.summary}
                onChange={(event) => update({ summary: event.target.value })}
                placeholder="用于产品卡展示的简短描述"
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">排序值（越小越靠前）</span>
              <input
                type="number"
                value={draft.sort_order}
                onChange={(event) => update({ sort_order: Number(event.target.value) || 0 })}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">评分</span>
              <input
                type="number"
                step="0.1"
                min={0}
                max={5}
                value={draft.rating}
                onChange={(event) => update({ rating: Number(event.target.value) || 0 })}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-xs font-medium text-foreground-700">累计授权量</span>
              <input
                type="number"
                value={draft.downloads}
                onChange={(event) => update({ downloads: Number(event.target.value) || 0 })}
                className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
              />
            </label>
          </section>

          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-foreground-700">详细介绍</span>
            <textarea
              rows={3}
              maxLength={500}
              value={draft.description}
              onChange={(event) => update({ description: event.target.value })}
              placeholder="产品详细介绍（500 字以内）"
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm leading-relaxed text-foreground-900 focus:border-primary-400 focus:outline-none"
            />
          </label>

          <section>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground-700">版本历史</span>
              <button
                type="button"
                onClick={() =>
                  update({
                    versions: [
                      ...draft.versions,
                      { version: "", date: new Date().toISOString().slice(0, 10), notes: "" },
                    ],
                  })
                }
                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-background-100"
              >
                <span className="flex h-3 w-3 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                添加版本
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {draft.versions.length === 0 && (
                <p className="rounded-md border border-dashed border-background-300 py-4 text-center text-xs text-foreground-500">
                  暂无版本记录，点击「添加版本」新增
                </p>
              )}
              {draft.versions.map((item, index) => (
                <div
                  key={index}
                  className="grid gap-2 rounded-md border border-background-200 bg-background-100 p-3 sm:grid-cols-[120px_140px_1fr_auto]"
                >
                  <input
                    type="text"
                    value={item.version}
                    onChange={(event) => updateVersion(index, { version: event.target.value })}
                    placeholder="版本号"
                    className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={item.date}
                    onChange={(event) => updateVersion(index, { date: event.target.value })}
                    placeholder="发布日期"
                    className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                  <input
                    type="text"
                    value={item.notes}
                    onChange={(event) => updateVersion(index, { notes: event.target.value })}
                    placeholder="更新说明"
                    className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      update({ versions: draft.versions.filter((_, i) => i !== index) })
                    }
                    className="flex h-8 w-8 cursor-pointer items-center justify-center self-center rounded-md text-foreground-500 transition-colors hover:bg-accent-50 hover:text-accent-700"
                    aria-label="删除该版本"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground-700">授权类型（版本类型）</span>
              <button
                type="button"
                onClick={() =>
                  update({
                    editions: [...draft.editions, { key: `edition-${draft.editions.length + 1}`, label: "", price: 0 }],
                  })
                }
                className="inline-flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-background-100"
              >
                <span className="flex h-3 w-3 items-center justify-center">
                  <i className="ri-add-line" />
                </span>
                添加授权类型
              </button>
            </div>
            <div className="mt-2 space-y-2">
              {draft.editions.length === 0 && (
                <p className="rounded-md border border-dashed border-background-300 py-4 text-center text-xs text-foreground-500">
                  暂无授权类型，点击「添加授权类型」新增
                </p>
              )}
              {draft.editions.map((item, index) => (
                <div
                  key={index}
                  className="grid gap-2 rounded-md border border-background-200 bg-background-100 p-3 sm:grid-cols-[1fr_140px_auto]"
                >
                  <input
                    type="text"
                    value={item.label}
                    onChange={(event) => updateEdition(index, { label: event.target.value })}
                    placeholder="类型名称，例如：专业版"
                    className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                  <input
                    type="number"
                    value={item.price}
                    onChange={(event) => updateEdition(index, { price: Number(event.target.value) || 0 })}
                    placeholder="价格 / 年"
                    className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      update({ editions: draft.editions.filter((_, i) => i !== index) })
                    }
                    className="flex h-8 w-8 cursor-pointer items-center justify-center self-center rounded-md text-foreground-500 transition-colors hover:bg-accent-50 hover:text-accent-700"
                    aria-label="删除该授权类型"
                  >
                    <i className="ri-delete-bin-line" />
                  </button>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 flex items-center justify-end gap-2 border-t border-background-200 bg-background-50 px-5 py-4 md:px-6">
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving}
            className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-save-line" />
            </span>
            {saving ? "保存中…" : "保存产品"}
          </button>
        </div>
      </div>
    </div>
  );
}